import { TFunction } from "i18next";
import { LiveDataRow } from "../models/LiveDataRow";
import { CheckItem, createCheckItem, RowMatch } from "./checkItems";
import { formatChecklistValue, formatOdometerValue } from "./checklistValues";
import {
  CheckKey,
  isCanRow,
  isFuelLevelPercentRow,
  isFuelLevelVolumeRow,
  isOdometerRow,
  isRawOdometerRow,
  isTachoCanCRow,
  isTachoD8Row,
  matchesCheck,
} from "./diagnosticMatchers";
import { buildIoxDevices, IoxFieldLabels, isIoxRow } from "./ioxDevices";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { getFirmwareValue } from "@/utilities/getFirmwareValue";

/** One titled block of the vehicle checklist. */
export interface ChecklistGroup {
  title: string;
  items: CheckItem[];
}

export interface BuildChecklistGroupsInput {
  /** The selected vehicle; the firmware line is read off it rather than out of the readings. */
  device?: DeviceWithVehicleInfo | null;
  /** Readings from the live window. */
  rows: LiveDataRow[];
  /** Readings from the wider history, used where the live window has nothing. */
  fallbackRows: LiveDataRow[];
  t: TFunction;
  /** The language the app is running in, for the number formats. */
  language: string;
}

/**
 * The vehicle checklist: what an installer has to see reported before signing a vehicle off.
 *
 * Every line answers one question — was this value reported, and what was it — and says whether
 * the answer came from the live window or only from the history. Three lines are lists rather
 * than single values, because their count is not known in advance: the CAN protocols, the
 * tachograph lines, and the add-on devices.
 */
export function buildChecklistGroups(input: BuildChecklistGroupsInput): ChecklistGroup[] {
  const { device, t, language } = input;

  // Every check wants the newest reading of some diagnostic, so the readings are reduced to the
  // newest one per diagnostic first, once. A day of history is tens of thousands of readings of
  // a couple of hundred diagnostics; without this each of the two dozen checks would walk all of
  // them again, on every refresh of the live window.
  const rows = newestPerDiagnostic(input.rows);
  const fallbackRows = newestPerDiagnostic(input.fallbackRows);

  const value = (row: LiveDataRow | undefined) => formatChecklistValue(row, language);
  const odometer = (row: LiveDataRow | undefined) => formatOdometerValue(row, language);

  function check(label: string, match: RowMatch, format = value): CheckItem {
    return createCheckItem({ label, rows, fallbackRows, match, format });
  }

  /** A check whose recognition rule lives in the matchers, addressed by its key. */
  function checkFor(key: CheckKey, label: string, format = value): CheckItem {
    return check(label, row => matchesCheck(key, row), format);
  }

  return [
    {
      title: t("liveData.checklist.general", "General values"),
      items: [
        check(
          t("liveData.checklist.rawOdometer", "Raw odometer"),
          isRawOdometerRow,
          odometer,
        ),
        check(t("liveData.checklist.odometer", "Odometer"), isOdometerRow, odometer),
        checkFor("intelligentHarness", t("liveData.checklist.intelligentHarness", "Intelligent harness type detected")),
        checkFor("standardHarness", t("liveData.checklist.standardHarness", "Standard harness detected")),
        firmwareItem(t, device),
        checkFor("deviceVoltage", t("liveData.checklist.deviceVoltage", "Telematics device voltage")),
        checkFor("ignition", t("liveData.checklist.ignition", "Ignition")),
        checkFor("vehicleSpeed", t("liveData.checklist.vehicleSpeed", "Vehicle speed")),
        checkFor("fmsStandardSoftwareVersion", t("liveData.checklist.fmsStandardSoftwareVersion", "Generic FMS standard software version")),
        ...canProtocolItems(rows, fallbackRows, t),
      ],
    },
    {
      title: t("liveData.checklist.combustion", "Combustion engine parameters"),
      items: [
        checkFor("engineSpeed", t("liveData.checklist.engineSpeed", "Engine speed")),
        check(t("liveData.checklist.fuelLevel", "Fuel level (%)"), isFuelLevelPercentRow),
        check(t("liveData.checklist.fuelLevelLiters", "Fuel level (l)"), isFuelLevelVolumeRow),
        checkFor("defLevel", t("liveData.checklist.defLevel", "DEF level")),
        checkFor("fuelConsumption", t("liveData.checklist.fuelConsumption", "Fuel consumption detected")),
      ],
    },
    {
      title: t("liveData.checklist.ev", "EV parameters"),
      items: [
        checkFor("stateOfCharge", t("liveData.checklist.soc", "SoC")),
        checkFor("chargingState", t("liveData.checklist.dcChargingState", "DC charging state")),
        checkFor("hvBatteryCurrent", t("liveData.checklist.hvBatteryCurrent", "HV-Battery current")),
        checkFor("hvBatteryVoltage", t("liveData.checklist.hvBatteryVoltage", "HV-Battery voltage")),
        checkFor("hvBatteryPower", t("liveData.checklist.hvBatteryPower", "HV-Battery power")),
        checkFor("evRange", t("liveData.checklist.evRange", "Range")),
        checkFor("cellVoltageHigh", t("liveData.checklist.cellVoltageHigh", "Cell voltage high")),
        checkFor("cellVoltageLow", t("liveData.checklist.cellVoltageLow", "Cell voltage low")),
        checkFor(
          "cellTemperatureHigh",
          t("liveData.checklist.cellTemperatureHigh", "Cell temperature high"),
        ),
        checkFor(
          "cellTemperatureLow",
          t("liveData.checklist.cellTemperatureLow", "Cell temperature low"),
        ),
      ],
    },
    {
      title: t("liveData.checklist.tachoIox", "Tachograph and IOX"),
      items: [
        ...onlyWhenReported(check(t("liveData.checklist.tachoC", "Tacho CANC"), isTachoCanCRow)),
        ...onlyWhenReported(check(t("liveData.checklist.tachoD8", "Tacho D8"), isTachoD8Row)),
        ...ioxItems(rows, fallbackRows, language, t),
      ],
    },
  ];
}

/**
 * The newest reading of each diagnostic.
 *
 * By timestamp rather than by array position: readings are paged and merged concurrently, so the
 * order they arrive in says nothing about which is newest.
 */
function newestPerDiagnostic(rows: LiveDataRow[]): LiveDataRow[] {
  const newest = new Map<string, LiveDataRow>();

  for (const row of rows) {
    const key = row.diagnosticId || row.diagnosticName;
    const previous = newest.get(key);
    if (previous === undefined || row.dateTime.getTime() > previous.dateTime.getTime()) {
      newest.set(key, row);
    }
  }

  return [...newest.values()];
}

function firmwareItem(t: TFunction, device: DeviceWithVehicleInfo | null | undefined): CheckItem {
  const firmware = getFirmwareValue(device);
  return {
    label: t("liveData.checklist.firmware", "Firmware"),
    value: firmware,
    found: firmware !== "",
    fromFallback: false,
  };
}

/**
 * One numbered line per CAN protocol the vehicle reported.
 *
 * A vehicle can speak several, and how many is exactly what the installer is checking, so this
 * is a list rather than a single line. Sorted by name so the numbering does not shuffle between
 * refreshes.
 */
function canProtocolItems(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  t: TFunction,
): CheckItem[] {
  const live = canProtocolRows(rows);
  const protocols = live.length > 0 ? live : canProtocolRows(fallbackRows);

  return protocols.map((row, index) => ({
    label: `${t("liveData.checklist.canProtocol", "CAN protocol")} ${index + 1}`,
    value: row.diagnosticName,
    found: true,
    fromFallback: live.length === 0,
    diagnosticId: row.diagnosticId || undefined,
  }));
}

/**
 * The CAN protocols a vehicle reported, by name.
 *
 * Read by the fleet-wide check, which shows the count as a column rather than a numbered list.
 * The checklist itself takes the rows, because its lines carry the diagnostic id as well.
 */
export function canProtocolNames(rows: LiveDataRow[]): string[] {
  return canProtocolRows(rows).map(row => row.diagnosticName);
}

/** Already one row per diagnostic by the time this runs, so no second dedupe is needed. */
function canProtocolRows(rows: LiveDataRow[]): LiveDataRow[] {
  return rows
    // An IOX-CAN adapter is add-on hardware, not a bus protocol, but its readings carry "CAN" as
    // a word — and the number of protocols is exactly what the installer is verifying here.
    .filter(row => isCanRow(row) && !isIoxRow(row))
    .sort((left, right) => left.diagnosticName.localeCompare(right.diagnosticName));
}

/** One line per add-on device, or a single not-found line when the vehicle reported none. */
function ioxItems(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  language: string,
  t: TFunction,
): CheckItem[] {
  const labels: IoxFieldLabels = {
    type: t("liveData.checklist.ioxType", "Type"),
    serial: t("liveData.checklist.ioxSerial", "S/N"),
    status: t("liveData.checklist.ioxStatus", "Status"),
  };
  const devices = buildIoxDevices(rows, fallbackRows, language, labels);

  if (devices.length === 0) {
    return [
      {
        label: t("liveData.checklist.ioxDevices", "IOX devices"),
        value: "",
        found: false,
        fromFallback: false,
      },
    ];
  }

  return devices.map(device => ({ ...device, found: true }));
}

/**
 * Keeps a line out of the list when the vehicle never reported it.
 *
 * The tachograph lines are not part of every installation, and an empty "not reported" line for
 * hardware the vehicle does not have would read as a fault.
 */
function onlyWhenReported(item: CheckItem): CheckItem[] {
  return item.found ? [item] : [];
}
